import {Component, OnInit, ChangeDetectorRef, OnDestroy, Input, ChangeDetectionStrategy} from '@angular/core';
import { Router } from '@angular/router';
import {ReplaySubject, Subscription} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from '@irb/app.service';
import { PageService } from '@irb-common/services/page.service'; 
import { IrbDashboardService } from '@irb/irb-dashboard/irb-dashboard.service';
import {ICashAnalysisDetails, IGlobalSettings} from '@irb/irb-dashboard/irb-dashboard.model';
import { AnalyticsService } from '@irb/irb-common/analytics/analytics.service';

@Component({
  standalone: false,
  selector: 'irb-module-cash-analysis',
  templateUrl: './module-cash-analysis.component.html',
  styleUrls: ['./module-cash-analysis.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ModuleCashAnalysisComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _data: ICashAnalysisDetails;
  chartData: any;
  colors: Array<string> = [
    "#C10B14",
    "#0092CC",
    "#267326"
  ];

  options: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    cutout: '75%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    }
  };
  private masterAccountSubscription: Subscription;
  selectedMasterAccounts: Array<string> = [];
  initailLoadPassed = false;
  @Input() customizableMode: boolean = false;
  @Input() singleWidth: boolean = true;
  @Input() globalSettings: IGlobalSettings;

  constructor(
    public as: AppService,
    public ps: PageService, 
    private dbs: IrbDashboardService, 
    private cdr: ChangeDetectorRef, 
    private router: Router,
    private ats: AnalyticsService
  ) {}

  ngOnInit() {
    if (this.customizableMode)
      return;

    this.ps.getUserDetails().pipe(takeUntil(this.destroyed$)).subscribe((d) => {   
      if(d) {
        this.masterAccountSubscription = this.dbs.currentMasterAccountData.subscribe(passedSelectedMasterAccounts => {
          if (this.initailLoadPassed) {
            this.data = null;
            this.selectedMasterAccounts = passedSelectedMasterAccounts;
            if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
              this.getCashAnalysisDetailsForSelectedAccounts();
            else
              this.getCashAnalysisDetailsForAllAccounts();
          }
        });

        this.initailLoadPassed = true;
        this.selectedMasterAccounts = this.dbs.selectedMasterAccounts;
        if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
          this.getCashAnalysisDetailsForSelectedAccounts();
        else
          this.getCashAnalysisDetailsForAllAccounts();
      }
    });
  }

  getCashAnalysisDetailsForAllAccounts() {
    this.dbs.getCashAnalysisDetails().pipe(takeUntil(this.destroyed$)).subscribe((d) => {
      this.data = d as ICashAnalysisDetails;
      this.update();
      this.cdr.detectChanges();
    });
  }

  getCashAnalysisDetailsForSelectedAccounts() {
    this.dbs.getCashAnalysisForMasters(this.selectedMasterAccounts).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (d: any) => {
        this.data = d as ICashAnalysisDetails;
        this.update();
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.masterAccountSubscription)
      this.masterAccountSubscription.unsubscribe();
  }

  update() {
    const a = (this.data.inShortFallCount / this.data.totalAccounts) * 100;
    const b = (this.data.inExcessCount / this.data.totalAccounts) * 100;
    const c = (this.data.inRangeCount / this.data.totalAccounts) * 100;
    const t = a + b + c;

    this.chartData = {
      datasets: [
          {
            data: (t > 0) ? [a,b,c] : [100],
            backgroundColor: (t > 0) ? this.colors : ["#E9E9E9"],
            hoverBackgroundColor: (t > 0) ? this.colors : ["#E9E9E9"],
            borderWidth: t > 0 ? 2 : 0,
            hoverBorderColor: t > 0 ? ["#FFFFFF"] : null
          }
      ]
    };
  }

  showCashReport() {
    this.ats.trackClickEvent("lc_cashreport");
    this.router.navigate(['/reports/cash-report']);
  }

  showCashReportWithCashInRange() {
    this.ats.trackClickEvent("lc_inrange");
    this.as.setToSession("CASH_REPORT_STATUS_FILTER_DEFAULT","In Range "+"("+this.data.inRangeCount+")");
    if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
      this.as.setToSession("DASHBOARD_SELECTED_MASTER_ACCOUNTS", this.selectedMasterAccounts);
    this.router.navigate(['/reports/cash-report']);
  }

  showCashReportWithCashShortFall() {
    this.ats.trackClickEvent("lc_shortfall");
    this.as.setToSession("CASH_REPORT_STATUS_FILTER_DEFAULT","Shortfall "+"("+this.data.inShortFallCount+")");
    if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
      this.as.setToSession("DASHBOARD_SELECTED_MASTER_ACCOUNTS", this.selectedMasterAccounts);
    this.router.navigate(['/reports/cash-report']);
  }

  showCashReportWithCashInExcess() {
    this.ats.trackClickEvent("lc_excess");
    this.as.setToSession("CASH_REPORT_STATUS_FILTER_DEFAULT","Excess "+"("+this.data.inExcessCount+")");
    if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
      this.as.setToSession("DASHBOARD_SELECTED_MASTER_ACCOUNTS", this.selectedMasterAccounts);
    this.router.navigate(['/reports/cash-report']);
  }

  get data() {
    return this._data;
  }

  set data(d: ICashAnalysisDetails) {
    this._data = d;
  }
}
